import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import type { DrawerPayloadAction } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { type TMemberType, createEmptyMemberType } from '@timeedit/types';
import api from '../services/api.service';
import type { TAppThunk, TRootState } from './store';

type TMembershipState = IDefaultReducerState & {
  allMemberships: TMemberType[];
  membershipMap: Record<number, TMemberType>;
  firstMembership: TMemberType | null;
  secondMembership: TMemberType | null;
};

export const initialState: TMembershipState = {
  loading: false,
  hasErrors: false,
  allMemberships: [],
  membershipMap: {},
  firstMembership: null,
  secondMembership: null,
};

// Slice
const slice = createSlice({
  name: 'memberships',
  initialState,
  reducers: {
    fetchMembershipsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchMembershipsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TMemberType>>
    ) => {
      state.allMemberships = payload.results;
      state.membershipMap = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchMembershipsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteMembershipRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteMembershipSuccess: (
      state,
      { payload: membershipId }: PayloadAction<number>
    ) => {
      state.allMemberships = state.allMemberships.filter(
        (memberType: TMemberType) => memberType.id !== membershipId
      );
      state.membershipMap[membershipId] = undefined;
      finishedLoadingSuccess(state);
    },
    deleteMembershipFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateMembershipRequest: (state) => {
      isLoadingRequest(state);
    },
    updateMembershipSuccess: (
      state,
      { payload }: PayloadAction<TMemberType>
    ) => {
      const index = state.allMemberships.findIndex(
        (memberType: TMemberType) => memberType.id === payload.id
      );
      state.allMemberships[index] = payload;
      state.membershipMap[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    updateMembershipFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstMembership: (state, { payload }: PayloadAction<TMemberType>) => {
      const previousFirstMembership = state.firstMembership;
      state.firstMembership = payload;
      if (previousFirstMembership?.id === 0) {
        state.allMemberships = state.allMemberships.filter(
          (membership: TMemberType) => membership.id !== 0
        );
      }
    },
    setSecondMembership: (state, { payload }: PayloadAction<TMemberType>) => {
      state.secondMembership = payload;
    },
    createUnsavedMembership: (
      state,
      { payload: newMembership }: PayloadAction<TMemberType>
    ) => {
      if (state.firstMembership?.id === 0) return;
      state.firstMembership = newMembership;
      state.allMemberships.unshift(newMembership);
      state.membershipMap[0] = newMembership;
    },
    discardChanges: (
      state,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        id: number;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstMembership = state.membershipMap[id];
      } else {
        state.secondMembership = state.membershipMap[id];
      }
    },
    changeMembership: <Key extends keyof TMemberType>(
      state: TMembershipState,
      { payload }: DrawerPayloadAction<TMemberType, Key>
    ) => {
      const { isFirstDrawer, key, value } = payload;
      if (isFirstDrawer) {
        state.firstMembership = {
          ...state.firstMembership,
          [key]: value,
        };
      } else {
        state.secondMembership = {
          ...state.secondMembership,
          [key]: value,
        };
      }
    },
    closeDrawer: (state, { payload: membershipId }: PayloadAction<number>) => {
      if (state.firstMembership?.id === membershipId) {
        state.firstMembership = null;
      } else if (state.secondMembership?.id === membershipId) {
        state.secondMembership = null;
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const membershipsLoading = (state: TRootState) =>
  state.memberships.loading;
export const membershipsSelector = (state: TRootState) =>
  state.memberships.allMemberships;
export const firstMembershipSelector = (state: TRootState) =>
  state.memberships.firstMembership;
export const secondMembershipSelector = (state: TRootState) =>
  state.memberships.secondMembership;
export const membershipMapSelector = (state: TRootState) =>
  state.memberships.membershipMap;

export const selectedIdsSelector = createSelector(
  [
    (state: TRootState) => state.memberships.firstMembership,
    (state: TRootState) => state.memberships.secondMembership,
  ],
  (firstMembership, secondMembership) =>
    _.compact([firstMembership, secondMembership]).map((mem) => mem.id)
);
// Actions
export const {
  fetchMembershipsRequest,
  fetchMembershipsSuccess,
  fetchMembershipsFailure,
  deleteMembershipRequest,
  deleteMembershipSuccess,
  deleteMembershipFailure,
  updateMembershipRequest,
  updateMembershipSuccess,
  updateMembershipFailure,
  setFirstMembership,
  setSecondMembership,
  createUnsavedMembership,
  discardChanges,
  changeMembership,
  closeDrawer,
} = slice.actions;

export const fetchMemberships = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchMembershipsRequest());
    const memberships = await api.get({ endpoint: `/member-types` });
    dispatch(fetchMembershipsSuccess(memberships));
  } catch (e) {
    dispatch(fetchMembershipsFailure());
    return console.error(e);
  }
};

export const createNewMembership = (): TAppThunk => (dispatch) => {
  const newMembership = { ...createEmptyMemberType(), id: 0, name: '' };
  dispatch(createUnsavedMembership(newMembership));
};

export const duplicateMembership =
  (membership: TMemberType): TAppThunk =>
  (dispatch) => {
    const newMembership = {
      ...membership,
      id: 0,
      name: membership.name + '-copy',
    };
    dispatch(createUnsavedMembership(newMembership));
  };

export const deleteMembership = createAppAsyncThunk<void, number>(
  'memberships/deleteMembership',
  async (memberTypeId, { dispatch }) => {
    try {
      dispatch(deleteMembershipRequest());
      await api.delete({
        endpoint: `/member-types/${memberTypeId}`,
      });
      dispatch(deleteMembershipSuccess(memberTypeId));
      dispatch(closeDrawer(memberTypeId));
    } catch (e) {
      dispatch(deleteMembershipFailure());
      return console.error(e);
    }
  }
);

export const saveMembership = createAppAsyncThunk<void, TMemberType>(
  'memberships/saveMembership',
  async (memberType, { dispatch }) => {
    try {
      dispatch(updateMembershipRequest());
      const { id, ...membershipBody } = memberType;
      if (id === 0) {
        await api.post({
          endpoint: `/member-types`,
          data: membershipBody,
        });
        dispatch(fetchMemberships());
      } else {
        const response = await api.patch({
          endpoint: `/member-types/${id}`,
          data: membershipBody,
        });
        dispatch(updateMembershipSuccess(response));
      }
      dispatch(closeDrawer(id));
    } catch (e: any) {
      dispatch(updateMembershipFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const checkMembershipSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { memberships } = getState();
    const { firstMembership, secondMembership, membershipMap } = memberships;
    if (isFirstDrawer && firstMembership && membershipMap[firstMembership.id]) {
      return checkSavedDisabled(
        firstMembership,
        membershipMap[firstMembership.id]
      );
    }
    if (
      !isFirstDrawer &&
      secondMembership &&
      membershipMap[secondMembership.id]
    ) {
      return checkSavedDisabled(
        secondMembership,
        membershipMap[secondMembership.id]
      );
    }

    return true;
  };

export const checkFirstMembershipExist =
  (): TAppThunk<boolean> => (_, getState) =>
    !!getState().memberships.firstMembership;
